import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Circle,
  Flex,
  HStack,
  Radio,
  RadioGroup,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
} from "@chakra-ui/react";
import PieByStage from "./PieByStage";
import StageByGroupBar from "./StageByGroupBar";
import {
  filterQuestionsByOwnerOrAssignee,
  getGroupDashboardData,
  getStageDashboardData,
  stageColors,
} from "../../dashboardDataUtils";
import { useAuth } from "../../../../../context";
import { useGetStagesOfRound } from "../../../../../hooks/query";
import { useSelector } from "react-redux";
import { H2Dashboard } from "components/text/headings";
import GapAnalysis from "../GapAnalysis/GapAnalysis";
import { User, Users } from "@phosphor-icons/react";

const TABS = [
  {
    name: "Stage",
    enabled: true,
  },
  //   { TODO
  //     name: "Complete",
  //     enabled: false,
  //   },
  //   {
  //     name: "Confidence",
  //     enabled: false,
  //   },
];

const Status = ({ questions }) => {
  const [selectedRequirement, setSelectedRequirement] = useState("all");
  const [dashboardQuestions, setDashboardQuestions] = useState(questions);
  const { user } = useAuth();

  const roundSelector = useSelector((state) => state.issbRound);

  const questionnaireId = roundSelector.selectedRound?.id;

  useEffect(() => {
    if (selectedRequirement === "all") {
      setDashboardQuestions(questions);
    } else {
      setDashboardQuestions(filterQuestionsByOwnerOrAssignee(questions, user.email));
    }
  }, [selectedRequirement, questions, user.email]);

  return (
    <Flex direction="column" gap={4} width="100%" mt={4}>
      <Flex direction="column" gap={4}>
        <Flex justify="space-between" align="center">
          <H2Dashboard>Round Progress</H2Dashboard>
          <ToggleButton options={[
			{label:"All", value:"all", icon: <Users size={18} />},
			{label:"My", value:"my", icon: <User size={18} />}]}
		  	selected={selectedRequirement} setSelected={setSelectedRequirement} />
        </Flex>

        <StageCharts questionnaireId={questionnaireId} questions={dashboardQuestions} />

        <Box mt={6} />
        <Accordion allowToggle defaultIndex={[0]}>
          <AccordionItem>
            <AccordionButton py={4}>
              <H2Dashboard>Gap Analysis</H2Dashboard>
              <AccordionIcon mr={0} ml="auto" boxSize={6} />
            </AccordionButton>
            <AccordionPanel mt={4} px={0}>
              <GapAnalysis questions={dashboardQuestions} />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Flex>
    </Flex>
  );
};

/**
 * 
 * @typedef {Object} Option
 * @property {string?} label
 * @property {string} value
 * @property {React.ReactNode?} icon
 * 
 * @param {Object} props
 * @param {Option[]} props.options: [{label, value, icon}]
 * @param {string} props.selected
 * @param {Function} props.setSelected
 * @param {string} props.colorOn Note: gray.400 provides a fair level of contrast See ESG-3013
 * @param {string} props.colorOff
 * @param {string} props.colorHover
 * @returns 
 */
const ToggleButton = ({options, selected, setSelected}) => {
	return (
		<HStack spacing={0} bg="gray.100" borderRadius="full" p="2px">
			{options.map(option => (
				<Button
					key={option.value}
					size="sm"
					minW="90px"
					onClick={() => setSelected(option.value)}
					bg={selected === option.value ? "gray.600" : "transparent"}
					color={selected === option.value ? "white" : "gray.500"}
					_hover={{ bg: selected === option.value ? "gray.600" : "gray.200" }}
					borderRadius="full"
					fontWeight={selected === option.value ? "600" : "normal"}
					rightIcon={option.icon}
				>{option.label || option.value}</Button>
			))}
		</HStack>
	);
};

const StageCharts = ({ questionnaireId, questions }) => {
  const { data: stages } = useGetStagesOfRound(questionnaireId, {
    cacheTime: 0,
  });

  const stageDashboardData = getStageDashboardData(questions, null);
  // filter to stages with data to simplify the legend below
  const stagesWithData = stages?.filter((stage) => stageDashboardData.find((sdd) => sdd.id === stage.name));

  return (
    <Flex direction="column" bg="gray.50" borderRadius="lg" border="solid 1px" borderColor="gray.200" p={4}>
      {/* legend */}
      <Table variant="simple" width="auto" size="sm" my={4}>
        <Tbody fontSize="body.s">
          <Flex as="tr" wrap="wrap">
            {stagesWithData?.map((stage) => (
              <Td key={stage.id}>
                <HStack>
                  <Circle size="10px" bg={stageColors[stage.stageType]} />
                  <Text color="teal">{stage.name}</Text>
                </HStack>
              </Td>
            ))}
          </Flex>
        </Tbody>
      </Table>

      <Flex direction="row" gap={8} wrap="wrap">
        <Box flex="0 0 450px" width="450px" height="auto">
          <PieByStage stageData={stageDashboardData} />
        </Box>
        <Box flex="1" minWidth="200px" height="auto">
          <StageByGroupBar groupData={getGroupDashboardData(questions, null)} />
        </Box>
      </Flex>
    </Flex>
  );
};

export default Status;
